import { questionTypeLabels, surveyButtonDestinations } from "./survey.constants";
import { snakeValue } from "./survey.mappers";

function validateMetadata(form, errors) {
  const title = String(form.title || "").trim();
  const description = String(form.description || "").trim();
  if (!title) errors.title = "Survey title is required.";
  else if (title.length > 255) errors.title = "Survey title cannot exceed 255 characters.";
  if (!description) errors.description = "Survey description is required.";

  if (form.reward) {
    if (form.reward.type !== "flash_credits") {
      errors.rewardType = "Select a supported reward type.";
    }
    const amount = Number(form.reward.amount);
    if (form.reward.amount === "" || !Number.isInteger(amount) || amount < 1) {
      errors.rewardAmount = "Flash Credit amount must be a positive whole number.";
    }
  }
}

function validationResult(errors) {
  return {
    isValid:
      !errors.title &&
      !errors.description &&
      !errors.rewardType &&
      !errors.rewardAmount &&
      !errors.buttonLabel &&
      !errors.buttonDestination &&
      !errors.questionsRoot &&
      !Object.keys(errors.questions || {}).length,
    errors,
  };
}

export function validateDraftSurvey(form) {
  const errors = { questions: {} };
  validateMetadata(form, errors);
  validateQuestions(form.questions || [], errors, false);
  return validationResult(errors);
}

function validateCompletionButton(form, errors) {
  const buttonConfig = form.buttonConfig || {};
  const buttonLabel = String(buttonConfig.label || "").trim();
  const allowedDestinations = surveyButtonDestinations.map((item) => item.value);
  if (!buttonLabel) errors.buttonLabel = "Button label is required.";
  else if (buttonLabel.length > 100) errors.buttonLabel = "Button label cannot exceed 100 characters.";
  if (!allowedDestinations.includes(buttonConfig.deepLinkUrl)) {
    errors.buttonDestination = "Select a destination screen.";
  }
}

function validateChoiceQuestion(question, current) {
  if (!(question.options || []).length && !(question.type === "single_choice" && question.allowOther)) {
    current.options = "Add at least one option.";
  }
  const optionErrors = {};
  const values = new Set();
  const ids = new Set();
  (question.options || []).forEach((option, optionIndex) => {
    const item = {};
    const label = String(option.label || "").trim();
    const value = snakeValue(label);
    if (!option.id || ids.has(option.id)) item.id = "Option ID must be unique.";
    if (!label) item.label = "Option text is required.";
    else if (!value) item.label = "Option text must contain letters or numbers.";
    else if (values.has(value) || value === "other") item.label = "Option text must produce a unique value.";
    ids.add(option.id);
    values.add(value);
    if (Object.keys(item).length) optionErrors[optionIndex] = item;
  });
  if (Object.keys(optionErrors).length) current.optionErrors = optionErrors;

  if (question.type === "single_choice" && question.allowOther) {
    if (!String(question.otherPlaceholder || "").trim()) {
      current.otherPlaceholder = "Other textbox placeholder is required.";
    }
    const otherLabel = String(question.otherLabel || "").trim();
    const regularLabels = (question.options || []).map((option) =>
      String(option.label || "").trim().toLowerCase(),
    );
    if (!otherLabel) current.otherLabel = "Other option label is required.";
    else if (regularLabels.includes(otherLabel.toLowerCase())) {
      current.otherLabel = "Other option label must be unique.";
    }
  }
}

function validateNumberQuestion(question, current) {
  const validation = question.validation || {};
  const step = Number(validation.step);
  if (validation.step === "" || !Number.isFinite(step) || step <= 0) {
    current.step = "Step must be a positive number.";
  }
  const min = validation.min === "" ? null : Number(validation.min);
  const max = validation.max === "" ? null : Number(validation.max);
  if (min !== null && !Number.isFinite(min)) current.min = "Minimum must be a valid number.";
  if (max !== null && !Number.isFinite(max)) current.max = "Maximum must be a valid number.";
  if (min !== null && max !== null && Number.isFinite(min) && Number.isFinite(max) && max < min) {
    current.max = "Maximum must be greater than or equal to minimum.";
  }
}

const questionValidators = {
  single_choice: validateChoiceQuestion,
  multi_choice: validateChoiceQuestion,
  number: validateNumberQuestion,
};

function validateQuestions(questions, errors, requireQuestions = true) {
  if (requireQuestions && !questions.length) {
    errors.questionsRoot = "Add at least one question before publishing.";
    return;
  }
  const questionIds = new Set();
  questions.forEach((question, questionIndex) => {
    const current = {};
    if (!question.id || questionIds.has(question.id)) current.id = "Question ID must be unique.";
    questionIds.add(question.id);
    if (!questionTypeLabels[question.type]) current.type = "Select a supported question type.";
    if (!String(question.question || "").trim()) current.question = "Question text is required.";
    const validator = questionValidators[question.type];
    if (validator) validator(question, current);
    if (Object.keys(current).length) errors.questions[questionIndex] = current;
  });
}

export function validatePublishableSurvey(form) {
  const errors = { questions: {} };
  validateMetadata(form, errors);
  validateCompletionButton(form, errors);
  validateQuestions(form.questions || [], errors);
  return validationResult(errors);
}
